import { type AnyProps, type ClusterFeature, type PointFeature } from "supercluster"

import { pluralize } from "@/helpers/pluralize"

declare global {
  interface Window {
    mapPins: MapPin[]
  }
}

type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement & {
  // Avoid having to cast `Node` to `HTMLDivElement` everywhere
  content: HTMLDivElement
}

interface ImageSlide {
  imgAlt: string
  imgHeight: string
  imgSrc: string
  imgWidth: string
  srcset1x: string
  srcset2x: string
  srcsizes: string
}

interface MapPin {
  firstCarouselImage: ImageSlide
  id: string
  name: string
  path: string
  position: google.maps.LatLngLiteral
  premiumLogo?: string
  promoOffersText?: string
  price: string
  originalPrice?: string
  overallRating?: {
    badgeClasses: string
    reviewDescription: string
    reviewRating: number
    numberOfReviews: number
  }
}

const ACTIVE_CLASS = "--active" as const
const HIGHLIGHTED_CLASS = "--highlighted" as const
const SEARCH_BAR_HEIGHT_CSS_PROP = "--search-bar-height"
const PROMO_BAR_HEIGHT_CSS_PROP = "--promo-bar-height"
const Z_INDEX = Object.freeze({
  CLUSTER: 0,
  PIN: 1,
  ACTIVE: 2,
  HIGHLIGHTED: 3
} as const)

const cardContent = (pin: MapPin): HTMLElement => {
  const content = document.createElement("div")

  content.className = "map-marker-card"
  content.innerHTML = `
    ${
      pin.promoOffersText
        ? `<div class="label-sm text-center uppercase text-white p-4">
            ${pin.promoOffersText}
          </div>`
        : ""
    }
    <div>
      <div data-img class="relative">
        <div
          data-controller="map-marker-carousel"
          data-map-marker-carousel-id-value="${pin.id}"
          data-map-marker-carousel-image-value='${pin.firstCarouselImage}'
        ></div>
        ${
          pin.premiumLogo
            ? `<img alt="Elysium Collection" src="${pin.premiumLogo}" width="84" height="28">`
            : ""
        }
      </div>
      <section>
        <header class="flex flex-col gap-8 xs:mr-24">
          <a href="${pin.path}" class="no-underline heading-xs" target="_blank">
            ${pin.name}
          </a>
          ${
            pin.overallRating
              ? `
                <section class="flex items-center gap-4">
                  <span class="${pin.overallRating.badgeClasses}">
                    ${pin.overallRating.reviewRating}
                  </span>
                  <span class="text-primary text-sm font-medium">
                    ${pin.overallRating.reviewDescription}
                  </span>
                  <span class="text-secondary text-xs">
                    (${pluralize(pin.overallRating.numberOfReviews, "review")})
                  </span>
                </section>
                `
              : ""
          }
        </header>
        <footer>
          <span class="text-secondary body-xs whitespace-nowrap">From</span>
          <div class="whitespace-nowrap flex items-center gap-4">
            ${
              pin.originalPrice
                ? `<span class="text-secondary body-sm line-through">${pin.originalPrice}</span>`
                : ""
            }
            <span class="text-primary heading-sm">${pin.price}</span>
          </div>
          <span class="text-secondary body-xs whitespace-nowrap">Per person</span>
        </footer>
      </section>
      <button class="btn-dismiss absolute top-8 leading-12 right-8 z-10 p-0 after:bg-primary after:rounded-full after:text-16 after:px-6" data-action="search-map#deactivateMarker:stop">
        <span>Close</span>
      </button>
    </div>
  `

  return content
}

const clusterMarkerContent = (count: number): HTMLDivElement => {
  const content = document.createElement("div")
  content.className = "search__map__cluster"
  content.textContent = count.toString()

  return content
}

const markerKey = (point: ClusterFeature<AnyProps> | PointFeature<AnyProps>): string => {
  const { pin } = point.properties

  // Pin id is venue id, but cluster ids are generated by Supercluster and might clash, so we
  // distinguish them with prefix.
  return pin ? `pin:${pin.id}` : `cluster:${point.id}`
}

const pinMarkerContent = (pin: MapPin): HTMLElement => {
  const template = document.getElementById("js-pin-marker-template") as HTMLTemplateElement
  const content = template.content.firstElementChild!.cloneNode(true) as HTMLElement
  const overallRating = content.querySelector<HTMLElement>(".js-pin-overall-rating")!
  const price = content.querySelector<HTMLElement>(".js-pin-price")!

  content.setAttribute("data-search-map-venue-id-param", pin.id)
  price.textContent = pin.price

  if (pin.overallRating) {
    overallRating.classList.add(...pin.overallRating.badgeClasses)
    overallRating.textContent = pin.overallRating.reviewRating.toString()
  } else {
    overallRating.hidden = true
  }

  return content
}

const pointsFromPins = (): PointFeature<AnyProps>[] =>
  window.mapPins.map((pin) => ({
    type: "Feature",
    properties: {
      cluster: false,
      id: pin.id,
      pin
    },
    geometry: {
      type: "Point",
      coordinates: [pin.position.lng, pin.position.lat]
    }
  }))

const setSearchBarHeightForCSS = (): void => {
  const NEW_SEARCH_BAR = document.body.getAttribute("data-new-search-bar") === "true"

  // Get search bar on the fly instead of on controller initialization to avoid potential race
  // conditions with Turbo.
  const searchBar = NEW_SEARCH_BAR
    ? document.getElementById("js-search-bar")!
    : document.getElementById("elm-search-container")!

  window.requestAnimationFrame(() => {
    const searchDomRect = searchBar.getBoundingClientRect()
    const promoDomRect = document.getElementById("js-sitewide-promo")?.getBoundingClientRect()

    document.body.style.setProperty(SEARCH_BAR_HEIGHT_CSS_PROP, `${searchDomRect.height}px`)
    document.body.style.setProperty(PROMO_BAR_HEIGHT_CSS_PROP, `${promoDomRect?.height || 0}px`)
  })
}

const toggleMarkerState = (
  marker: AdvancedMarkerElement,
  stateClass: typeof ACTIVE_CLASS | typeof HIGHLIGHTED_CLASS,
  force?: boolean
): void => {
  const { classList } = marker.content

  classList.toggle(stateClass, force)

  if (classList.contains(ACTIVE_CLASS)) {
    marker.zIndex = Z_INDEX.ACTIVE
  } else if (classList.contains(HIGHLIGHTED_CLASS)) {
    marker.zIndex = Z_INDEX.HIGHLIGHTED
  } else {
    marker.zIndex = Z_INDEX.PIN
  }
}

export {
  type AdvancedMarkerElement,
  type ImageSlide,
  type MapPin,
  ACTIVE_CLASS,
  HIGHLIGHTED_CLASS,
  Z_INDEX,
  cardContent,
  clusterMarkerContent,
  markerKey,
  pinMarkerContent,
  pointsFromPins,
  setSearchBarHeightForCSS,
  toggleMarkerState
}
